// Identity Step - Clean Kustom Style
// First step: Just email and postal code

import { useState, useRef } from 'react';
import { useCheckoutStore } from '../store/checkout';
import { getSavedAddresses } from '../api/client';
import { getMarketConfig } from '../config/market';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import styles from './IdentityStep.module.css';

export function IdentityStep() {
  const setCustomer = useCheckoutStore((s) => s.setCustomer);
  const setShippingAddress = useCheckoutStore((s) => s.setShippingAddress);
  const setSavedAddresses = useCheckoutStore((s) => s.setSavedAddresses);
  const sessionId = useCheckoutStore((s) => s.sessionId);
  const customer = useCheckoutStore((s) => s.customer);
  const shippingAddress = useCheckoutStore((s) => s.shippingAddress);
  const nextStep = useCheckoutStore((s) => s.nextStep);
  const isLoading = useCheckoutStore((s) => s.isLoading);
  const setLoading = useCheckoutStore((s) => s.setLoading);
  const merchant = useCheckoutStore((s) => s.merchant);
  const locale = useCheckoutStore((s) => s.locale);
  const existingCustomer = useCheckoutStore((s) => s.customer);
  const existingAddress = useCheckoutStore((s) => s.shippingAddress);
  
  // Market config for locale-aware labels and validation
  const market = getMarketConfig(locale);
  const L = market.labels;
  
  // Get URLs from merchant storeInfo
  const termsURL = merchant?.storeInfo?.termsAndConditionsURL;
  const privacyURL = merchant?.storeInfo?.privacyPolicyURL;
  
  // Pre-fill from session data if merchant sent customer info
  // Fall back to the unsubmitted draft, so a reload does not blank the form.
  const draft = useCheckoutStore((s) => s.draft);
  const setDraft = useCheckoutStore((s) => s.setDraft);
  const [email, setEmail] = useState(existingCustomer?.email || draft.email || '');
  const [postalCode, setPostalCode] = useState(existingAddress?.postalCode || draft.postalCode || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Track what we've already looked up to avoid duplicate calls
  const lookupDone = useRef<string | null>(null);

  const validateEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const validatePostalCode = (value: string) => {
    return market.postalCodePattern.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    if (!email) {
      newErrors.email = L.emailRequired;
    } else if (!validateEmail(email)) {
      newErrors.email = L.emailInvalid;
    }
    
    if (!postalCode) {
      newErrors.postalCode = L.postalCodeRequired;
    } else if (!validatePostalCode(postalCode)) {
      newErrors.postalCode = L.postalCodeInvalid;
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    
    // Lookup saved addresses matching BOTH email AND postal code
    const lookupKey = `${email}:${postalCode}`;
    if (lookupDone.current !== lookupKey) {
      lookupDone.current = lookupKey;
      
      try {
        const response = sessionId ? await getSavedAddresses(email, sessionId, postalCode) : { data: undefined };
        
        if (response.data?.addresses?.length) {
          // Store matching addresses for autofill on next step
          setSavedAddresses(response.data.addresses.map(a => ({
            ...a,
            countryCode: a.countryCode || 'NO',
          })));
        } else {
          setSavedAddresses([]);
        }
      } catch (e) {
        setSavedAddresses([]);
      }
    }
    
    /**
     * Add the email; keep everything the shop already told us.
     *
     * This wrote `{email, name: '', phone: ''}`, so a merchant who prefilled
     * their customer's name and number had both erased the moment that
     * customer typed an email — and then had to type them again on the next
     * step. The prefill worked; this threw it away one screen later, which is
     * why it looked like it had never happened.
     */
    setCustomer({
      ...customer,
      email,
      name: customer?.name || '',
      phone: customer?.phone || '',
    });

    /**
     * Same for the address: the postal code is new, the rest is not ours to
     * clear. A merchant-supplied street and city survive to the address step,
     * where they are what the customer expects to see already filled in.
     */
    setShippingAddress({
      ...shippingAddress,
      streetAddress: shippingAddress?.streetAddress || '',
      postalCode,
      city: shippingAddress?.city || '',
      countryCode: shippingAddress?.countryCode || market.countryCode,
    });
    
    setLoading(false);
    nextStep();
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{locale === 'no' || locale === 'nb-NO' ? 'Dine opplysninger' : locale === 'sv' ? 'Dina uppgifter' : locale === 'da' ? 'Dine oplysninger' : locale === 'fi' ? 'Tietosi' : 'Your details'}</h1>
      <p className={styles.subtitle}>
        {locale === 'sv' ? 'Fält märkta med stjärna (*) är obligatoriska.' : locale === 'da' ? 'Felter markeret med stjerne (*) skal udfyldes.' : locale === 'fi' ? 'Tähdellä (*) merkityt kentät ovat pakollisia.' : locale === 'en' ? 'Fields marked with an asterisk (*) are required.' : 'Felt som er merket med stjerne (*) må fylles ut.'}
      </p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.inputGroup}>
          <Input
            label={L.emailLabel}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setDraft({ email: e.target.value });
              setErrors(prev => ({ ...prev, email: '' }));
              lookupDone.current = null; // Reset when email changes
            }}
            error={errors.email}
            required
            autoComplete="email"
          />
          <Input
            label={L.postalCodeLabel}
            type="text"
            inputMode="numeric"
            maxLength={market.postalCodeLength}
            value={postalCode}
            onChange={(e) => {
              const value = e.target.value.replace(/[^\d\s]/g, '');
              setPostalCode(value);
              setDraft({ postalCode: value });
              setErrors(prev => ({ ...prev, postalCode: '' }));
              lookupDone.current = null; // Reset when postal code changes
            }}
            error={errors.postalCode}
            required
            autoComplete="postal-code"
          />
        </div>

        <div className={styles.buttonWrapper}>
          <Button type="submit" loading={isLoading}>
            {L.continueButton}
          </Button>
        </div>
      </form>

      {(termsURL || privacyURL) && (
        <p className={styles.terms}>
          {L.termsIdentityPrefix}{' '}
          {termsURL && (
            <a href={termsURL} target="_blank" rel="noopener noreferrer">
              {L.termsLink}
            </a>
          )}
          {termsURL && privacyURL && ` ${L.andWord} `}
          {privacyURL && (
            <a href={privacyURL} target="_blank" rel="noopener noreferrer">
              {L.privacyLink}
            </a>
          )}
          .
        </p>
      )}
    </div>
  );
}
